import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Text as BaseText, Flex, Box, ButtonFilled, SvgIcon } from 'Components/Base';

import { getVideoVariant } from './Util';
import { VIDEO_SIZE, VIDEO_VARIANTS } from './constant';

export const VideoOverlayWrapper = styled((props) => (
  <Flex justifyContent="center" alignItems="center" flexDirection="column" {...props} />
))`
  display: ${({ active }) => (active ? 'flex' : 'none')};
`;

export const Text = (props) => (
  <BaseText fontWeight="medium" fontFamily="content" color="videoOverlayDescription" {...props} />
);

export const Button = styled(({ text, icon, buttonProps, ...props }) => (
  <ButtonFilled borderRadius="100px" {...buttonProps.sysButtonProps} {...props}>
    <Flex justifyContent={'center'} alignItems={'center'}>
      <SvgIcon mr={'12px'} icon={icon} size="24px" {...buttonProps.iconProps} />
      <Text
        color="inherit"
        fontWeight="medium"
        fontFamily="content"
        lineHeight="title"
        {...buttonProps.messageProps}
      >
        {text}
      </Text>
    </Flex>
  </ButtonFilled>
))`
  ${({ theme: { colors = {} } }) => css`
    && {
      border: 1px solid white;
      background: ${colors.primary};
    }
  `}
`;

const VideoOverlayContent = ({
  active,
  currentSubsectionTitle,
  nextSubsectionTitle,
  variant,
  videoSize,
  onReplayClick,
  onNextSubsectionClick,
  onResumeClick,
  ...props
}) => {
  const { t } = useTranslation('classroom');
  const theme = useTheme();
  const { width: videoWidth } = videoSize ?? {};
  const controlButtonProps =
    variant === VIDEO_VARIANTS.ENDED
      ? {
          text: t('VIDEO_OVERLAY.ENDED.BUTTON.REPLAY'),
          icon: SvgIcon.ICON_LIST.REPLAY,
          onClick: onReplayClick,
        }
      : {
          text: t('VIDEO_OVERLAY.PAUSED.BUTTON.RESUME'),
          icon: SvgIcon.ICON_LIST.PLAY,
          onClick: onResumeClick,
        };

  const { headerProps, titleProps, buttonProps } = getVideoVariant(videoWidth, theme);

  return (
    <VideoOverlayWrapper active={active} videoSize={videoWidth} {...props}>
      {videoWidth >= VIDEO_SIZE.LARGE && currentSubsectionTitle && (
        <Box
          alignSelf="center"
          display={
            variant === VIDEO_VARIANTS.ENDED && videoWidth < theme._breakpoints[0]
              ? ['none', 'block']
              : 'block'
          }
        >
          <Text color="videoOverlayHeader" textAlign={'center'} {...headerProps}>
            {t('VIDEO_OVERLAY.PAUSED.LEARNING')}
          </Text>
          <Text textAlign={'center'} {...titleProps}>
            {currentSubsectionTitle}
          </Text>
        </Box>
      )}
      {variant === VIDEO_VARIANTS.ENDED && videoWidth >= VIDEO_SIZE.MEDIUM && nextSubsectionTitle && (
        <Fragment>
          <Text color="videoOverlayHeader" {...headerProps}>
            {t('VIDEO_OVERLAY.ENDED.NEXT_SUBSECTION')}
          </Text>
          <Text textAlign={'center'} {...titleProps}>
            {nextSubsectionTitle}
          </Text>
        </Fragment>
      )}
      <Flex justifyContent="space-around" alignItems="center">
        {videoWidth >= VIDEO_SIZE.LARGE && (
          <Button my={1} mx={4} buttonProps={buttonProps} {...controlButtonProps} />
        )}
        {videoWidth >= VIDEO_SIZE.TINY && nextSubsectionTitle && (
          <Fragment>
            {videoWidth >= VIDEO_SIZE.SMALL ? (
              <Button
                my={1}
                mx={4}
                text={t('VIDEO_OVERLAY.ENDED.BUTTON.NEXT_SUBSECTION')}
                icon={SvgIcon.ICON_LIST.SKIP_NEXT}
                onClick={onNextSubsectionClick}
                buttonProps={buttonProps}
              />
            ) : (
              <SvgIcon
                style={{ cursor: 'pointer' }}
                icon={SvgIcon.ICON_LIST.SKIP_NEXT}
                size="24px"
                onClick={onNextSubsectionClick}
              />
            )}
          </Fragment>
        )}
      </Flex>
    </VideoOverlayWrapper>
  );
};

VideoOverlayContent.VARIANTS = VIDEO_VARIANTS;

VideoOverlayContent.propTypes = {
  active: PropTypes.bool,
  currentSubsectionTitle: PropTypes.string,
  nextSubsectionTitle: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(VIDEO_VARIANTS)).isRequired,
  onReplayClick: PropTypes.func,
  onNextSubsectionClick: PropTypes.func,
  onResumeClick: PropTypes.func,
};

VideoOverlayContent.defaultProps = {
  active: false,
  onReplayClick: () => {},
  onNextSubsectionClick: () => {},
  onResumeClick: () => {},
};

export default VideoOverlayContent;
